import React, { useMemo } from 'react'
import { UpdateBudgetScreen } from 'src/app/UpdateBudget/UpdateBudget'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import BudgetForm, {
  BudgetFlatFormValues,
} from 'src/components/BudgetForm/BudgetForm'
import { CreditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardListModel,
  CreditCardType,
} from 'src/models/creditCard.models'
import { UserListModel } from 'src/models/user.models'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'

interface UpdateBudgetStandardContentProps {
  creditCard: CreditCardListModel
  recipient: UserListModel
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  rcnOptions?: CreditCardOption[]
  puttingCreditCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  setScreen: (val: UpdateBudgetScreen) => void
  putCreditCard: (body: Partial<PostCreditCardRequest>) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
}

const UpdateBudgetStandardContent: React.FC<
  UpdateBudgetStandardContentProps
> = (props) => {
  const translate = useTranslate()

  const budgetStandardActiveImage = useColorOrGrayscaleImage(
    ImageNames.BudgetStandardActive,
  )
  const baseFlatFields = useMemo(
    (): BudgetFlatFormValues => ({
      displayName: props.creditCard.displayName,
      email: props.recipient.email,
      creditCardId: props.creditCard.parentCreditCardId,
      issuingLimitDollars: props.creditCard.issuingLimit,
      untilMoment: props.creditCard.untilTimezonedMoment,
      isUnlimited: props.creditCard.issuingLimitCents === undefined,
      isForever: props.creditCard.untilTimezonedMoment === undefined,
      currency: props.creditCard.currency,
      notes: props.creditCard.notes,
    }),
    [props.creditCard, props.recipient],
  )

  const initialFlatFields = useMemo(
    (): BudgetFlatFormValues => ({
      displayName: props.creditCard.displayName,
      email: props.recipient.email,
      creditCardId: props.creditCard.parentCreditCardId,
      issuingLimitDollars: props.creditCard.issuingLimit,
      untilMoment: props.creditCard.untilTimezonedMoment,
      isUnlimited: props.creditCard.issuingLimitCents === undefined,
      isForever: props.creditCard.untilTimezonedMoment === undefined,
      currency: props.creditCard.currency,
      notes: props.creditCard.notes,
    }),
    [props.creditCard, props.recipient],
  )

  const onSuccessfulSubmit = (flatFields: BudgetFlatFormValues): void => {
    props.putCreditCard({
      creditCardId: flatFields.creditCardId,
      emails: flatFields.emails,
      displayName: flatFields.displayName,
      issuingLimitCents:
        flatFields.issuingLimitDollars !== undefined && !flatFields.isUnlimited
          ? toCents(flatFields.issuingLimitDollars)
          : undefined,
      setToUnlimited: flatFields.isUnlimited,
      until:
        flatFields.untilMoment !== undefined && !flatFields.isForever
          ? flatFields.untilMoment.format('YYYY-MM-DD')
          : undefined,
      type: CreditCardType.Delegate,
      reset: false,
      notes: flatFields.notes,
    })
  }

  return (
    <ActionRightFlyoutContent
      title={translate('Update Budget')}
      specificElements={{
        subtitle: translate('Standard'),
        imageSrc: budgetStandardActiveImage,
      }}
    >
      <BudgetForm
        topText={props.creditCard.updateFormTopText}
        buttonText={translate('Update Budget')}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.puttingCreditCard}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        rcnOptions={props.rcnOptions}
        recurrenceEditable={false}
        initialFlatFields={initialFlatFields}
        updateProps={{
          baseFlatFields,
          timezone: props.creditCard.timezone,
        }}
        userFieldLabel={translate('Recipient')}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        isFormDisabled={false}
        suggestedProps={{
          showSuggested: true,
          showOwnUserInSuggested: true,
        }}
      />
    </ActionRightFlyoutContent>
  )
}

export default UpdateBudgetStandardContent
